import { Fragment } from "react";
import { ChevronDownIcon } from "@heroicons/react/outline";
import { AiOutlineEye, AiOutlineEyeInvisible } from "react-icons/ai";
import { Listbox, Transition } from "@headlessui/react";
import { ReactComponent as BankCard } from '../../../../../assets/svgIcons/bank-card.svg'

function classNames(...classes) {
    return classes.filter(Boolean).join(" ");
}

export default function UserWallets({
    wallets,
    selectedWallet,
    onSelectWallet,
    showBalance,
    onToggleBalance
}) {
    return (
        <div className="mb-6">
            <div className="flex flex-col md:flex-row gap-4 bg-white dark:bg-[#010101] rounded-xl px-4 py-6" style={{ boxShadow: '0 18px 40px 0 rgba(112, 144, 176, 0.12)' }}>
                {/* Current Wallet Selector */}
                <div className="flex-1">
                    <label className="block text-sm text-gray-600 dark:text-gray-400 mb-2">
                        Current Wallet
                    </label>
                    <Listbox value={selectedWallet} onChange={onSelectWallet}>
                        <div className="relative">
                            <Listbox.Button className="relative w-full cursor-pointer rounded-lg bg-white dark:bg-[#121212] py-3 pl-4 pr-10 text-left border border-gray-300 dark:border-gray-700 focus:outline-none focus:ring-2 focus:ring-primary">
                                <span className="flex items-center">
                                    <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-green-100 dark:bg-green-900 mr-2">
                                        <span className="text-xs font-bold text-green-600 dark:text-green-300">
                                            {selectedWallet?.currency_code?.substring(0, 1)}
                                        </span>
                                    </span>
                                    <span className="block truncate font-medium text-gray-900 dark:text-white">
                                        {selectedWallet?.currency_code} Wallet
                                    </span>
                                </span>
                                <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                                    <ChevronDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                                </span>
                            </Listbox.Button>
                            <Transition
                                as={Fragment}
                                leave="transition ease-in duration-100"
                                leaveFrom="opacity-100"
                                leaveTo="opacity-0"
                            >
                                <Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white dark:bg-gray-800 py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                                    {wallets?.map((wallet) => (
                                        <Listbox.Option
                                            key={wallet.id}
                                            className={({ active }) =>
                                                classNames(
                                                    active ? "bg-primary bg-opacity-10" : "",
                                                    "relative cursor-pointer select-none py-2 pl-3 pr-9"
                                                )
                                            }
                                            value={wallet}
                                        >
                                            {({ selected }) => (
                                                <span className="flex items-center">
                                                    <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-green-100 dark:bg-green-900 mr-2">
                                                        <span className="text-xs font-bold text-green-600 dark:text-green-300">
                                                            {wallet.currency_code?.substring(0, 1)}
                                                        </span>
                                                    </span>
                                                    <span
                                                        className={classNames(
                                                            selected ? "font-semibold" : "font-normal",
                                                            "block truncate text-gray-900 dark:text-white"
                                                        )}
                                                    >
                                                        {wallet.currency_code} Wallet
                                                    </span>
                                                </span>
                                            )}
                                        </Listbox.Option>
                                    ))}
                                </Listbox.Options>
                            </Transition>
                        </div>
                    </Listbox>
                </div>

                {/* Vertical Divider - Only visible on Desktop */}
                <div className="hidden md:block w-px bg-gray-200 dark:bg-gray-700 self-stretch"></div>

                {/* Available Balance */}
                <div className="flex-1 rounded-lg px-4 relative">
                    <div className="flex items-start justify-between">
                        <div className="flex-1">
                            <div className="flex items-center mb-1">
                                <span className="text-sm text-gray-700 dark:text-gray-300">
                                    Available Balance
                                </span>
                                <button
                                    onClick={onToggleBalance}
                                    className="ml-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
                                >
                                    {showBalance ? (
                                        <AiOutlineEye className="h-4 w-4" />
                                    ) : (
                                        <AiOutlineEyeInvisible className="h-4 w-4" />
                                    )}
                                </button>
                            </div>
                            {showBalance ? (
                                <div className="text-3xl font-bold text-gray-900 dark:text-white">
                                    {selectedWallet?.currency_code}
                                    {parseFloat(selectedWallet?.balance || 0).toLocaleString("en-US", {
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2,
                                    })}
                                </div>
                            ) : (
                                <div className="text-3xl font-bold text-gray-900 dark:text-white">
                                    ••••••
                                </div>
                            )}
                            <div className="flex items-center bg-[#F5F5F5] rounded-xl w-fit mt-2 text-xs text-gray-600 dark:text-gray-400 py-1 px-3">
                                <span className="w-2 h-2 bg-[#6BB229] rounded-full mr-1"></span>
                                Last updated: Just now
                            </div>
                        </div>
                        <div className="w-12 h-12 rounded-lg flex items-center justify-center">
                            {/* <svg
                                className="w-6 h-6 text-orange-600 dark:text-orange-300"
                                fill="currentColor"
                                viewBox="0 0 20 20"
                            >
                                <path d="M4 4a2 2 0 00-2 2v1h16V6a2 2 0 00-2-2H4z" />
                                <path
                                    fillRule="evenodd"
                                    d="M18 9H2v5a2 2 0 002 2h12a2 2 0 002-2V9zM4 13a1 1 0 011-1h1a1 1 0 110 2H5a1 1 0 01-1-1zm5-1a1 1 0 100 2h1a1 1 0 100-2H9z"
                                    clipRule="evenodd"
                                />
                            </svg> */}
                            <BankCard className="w-[80px] h-[80px]" />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
